// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Route,
    Switch,
    Redirect,
    useRouteMatch,
    useLocation,
    matchPath
} from 'react-router-dom';

import { SidebarWithLink } from 'common/components/core/sidebar';
import { useGetRoutes } from './routes';

import theme from './Settings.scss';

const Settings = (): React.Node => {
    const { t } = useTranslation();
    const match = useRouteMatch();
    const location = useLocation();
    const routes = useGetRoutes();
    const sections = React.useMemo(() => {
        return [
            {
                id: 'settings',
                title: t('container:settings.sidebar.section.settings'),
                nonSelectable: true,
                disabled: false,
                subsections: routes
            }
        ];
    }, [routes, t]);

    const flattenedRoutes = React.useMemo(() => {
        return sections.reduce((acc, section) => {
            if (section.path && section.Component) {
                return [
                    ...acc,
                    {
                        id: section.id,
                        path: section.path,
                        Component: section.Component
                    }
                ];
            }

            if (section.subsections) {
                return [
                    ...acc,
                    ...section.subsections
                        .filter(subsection => !subsection.disabled)
                        .map(subsection => ({
                            id: subsection.id,
                            path: subsection.path,
                            Component: subsection.Component
                        }))
                ];
            }

            return acc;
        }, []);
    }, [sections]);

    const selectedSectionId = React.useMemo(() => {
        const customMatch = matchPath(location.pathname, {
            path: `${match.url}/:id`,
            exact: false,
            strict: false
        });
        const id = customMatch?.params.id;
        return id;
    }, [location, match]);

    return (
        <div className={theme.container}>
            <SidebarWithLink
                sections={sections}
                selectedSectionId={selectedSectionId}
                theme={theme}
            />
            <div className={theme.routesWrap}>
                <Switch>
                    {flattenedRoutes.map(route => (
                        <Route
                            key={route.path}
                            path={`${match.path}/${route.id}`}
                            component={route.Component}
                        />
                    ))}
                    {routes.length > 0 ? (
                        <Redirect
                            exact
                            from={match.path}
                            to={flattenedRoutes[0].path}
                        />
                    ) : null}
                </Switch>
            </div>
        </div>
    );
};

export default Settings;
